// ── Sessions ("/sessions") — the operational schedule (Wave 6) ──────────────
// ONE honest read (the role-scoped session list) routed through useHonestQuery ->
// <HonestState>. The page NEVER hand-rolls state branching: a gate / permission
// -denied / unavailable / error response renders via <HonestState> (tagged
// data-honest-posture), NEVER as a plain empty table. Real content (the DataTable
// + filter toolbar) renders only on the ready-with-data (or honest-empty) path.
//
// ── PARTICIPANT ISOLATION (blueprint §6 gate #1, FR-PDASH-001) ──────────────
// The ADMIN Sessions module at `/sessions` is for STAFF / coach / company_admin /
// cohort_lead ONLY. A `participant` is DENIED that module entirely. The legacy
// `/meetings` compatibility alias is the one allowed participant entry point:
// it renders the existing own-session ParticipantSessionsSection instead of the
// staff schedule, so participants never see another cohort's sessions or staff
// operations controls.
//
// The list is a READ-AND-NAVIGATE surface: every staff row links to the session
// detail where the per-session WRITE flows live. Create Series is a cohort-scoped
// flow reached from the cohort workspace / session detail — NOT a list-level
// button. The participant alias is status-only and has no operational shell.
//

import { Link } from "react-router-dom";
import { Button, CollectionToolbar, IconCalendar, PageFrame, ProTable } from "@vjal/ui";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { HonestState } from "../../components/honest-state";
import { honestGateFromError, postureForCoachingError, referenceFromError } from "../../services/coaching-error-posture";
import { getCoachingServices } from "../../services/coaching-service-provider";
import type { CoachingCohortDto } from "../../services/coaching-dtos";
import type { ProductScreenContext } from "../product-route-contracts";
import { useCoachingProfile } from "../../hooks/useCoachingQueries";
import { useWalkReadinessHold, useWalkReadyMarker } from "../../lib/walk-readiness";
import { ParticipantSessionsSection } from "../participant-dashboard/participant-sessions-section";
import { resolveParticipantZone } from "../participant-dashboard/participant-time";
import {
  cohortOptionsFromSessions,
  useSessionsCollection,
  type CohortFilterOption,
  type SessionsCollectionFetcher
} from "./sessions-collection";
import { canAccessSessionsModule } from "./sessions-helpers";

const services = getCoachingServices();

export function SessionsPage(context: ProductScreenContext) {
  if (context.role === "participant" && context.routePath === "/meetings") {
    return <ParticipantMeetingsSurface />;
  }
  return <OperationalSessionsPage {...context} />;
}

function ParticipantMeetingsSurface() {
  const profileQuery = useCoachingProfile();
  const [sessionsReady, setSessionsReady] = useState(false);
  const zone = resolveParticipantZone(profileQuery.data?.timezone, null);
  useWalkReadyMarker(!profileQuery.isLoading && sessionsReady);
  return <ParticipantSessionsSection zone={zone} onReady={setSessionsReady} />;
}

function OperationalSessionsPage({ role, routePath }: ProductScreenContext) {
  const moduleDenied = !canAccessSessionsModule(role);
  const [rowCohortOptions, setRowCohortOptions] = useState<CohortFilterOption[]>([]);
  const [initialScheduleReadPending, setInitialScheduleReadPending] = useState(true);
  const cohortListQuery = useQuery({
    queryKey: ["coaching", "sessions", "cohorts"],
    queryFn: () => services.cohorts?.list?.() ?? Promise.resolve({ items: [] }),
    enabled: !moduleDenied
  });
  useWalkReadinessHold(!moduleDenied && cohortListQuery.isLoading && !cohortListQuery.data && !cohortListQuery.isError);
  const cohortOptions = useMemo((): CohortFilterOption[] =>
    (cohortListQuery.data?.items ?? [])
      .map((cohort: CoachingCohortDto) => ({
        id: (cohort.id ?? "").trim(),
        label: (cohort.name ?? "").trim() || (cohort.id ?? "").trim()
      }))
      .filter((option) => option.id.length > 0),
    [cohortListQuery.data?.items]
  );
  const fetchSessions = useCallback<SessionsCollectionFetcher>(
    async ({ page, pageSize, q, filters }) => {
      if (moduleDenied) return { rows: [], total: 0 };
      const response = await services.sessions.list({
        page,
        pageSize,
        q: q.trim() || undefined,
        cohort_id: typeof filters.cohort_id === "string" ? filters.cohort_id : undefined,
        coach_user_id: typeof filters.coach_user_id === "string" ? filters.coach_user_id : undefined,
        status: typeof filters.status === "string" ? filters.status : undefined
      });
      const rows = response.items.map((row) => ({ ...row }));
      setRowCohortOptions(cohortOptionsFromSessions(rows));
      return { rows, total: response.total ?? response.items.length };
    },
    [moduleDenied]
  );
  const effectiveCohortOptions = cohortOptions.length > 0 ? cohortOptions : rowCohortOptions;
  const collection = useSessionsCollection(fetchSessions, effectiveCohortOptions, routePath);
  useEffect(() => {
    if (collection.state.status === "success" || collection.state.status === "error") setInitialScheduleReadPending(false);
  }, [collection.state.status]);
  useWalkReadinessHold(
    !moduleDenied &&
      initialScheduleReadPending &&
      (collection.state.status === "idle" || collection.state.status === "loading")
  );

  if (moduleDenied) {
    return (
      <section aria-label="Sessions" className="col-span-12" data-testid="sessions-page">
        <div className="rounded-xl border border-line bg-white">
          <HonestState
            posture="permission-denied"
            title="You don't have access to the sessions schedule"
            message="The sessions schedule is for coaching staff. Your own sessions are available on your dashboard."
          />
        </div>
      </section>
    );
  }

  const hasScopedView = Boolean(collection.state.q.trim()) || collection.activeFilters.length > 0 || collection.state.page > 1;
  const noRowsOnScopedPage = hasScopedView && collection.uiState === "ready" && collection.rows.length === 0;
  const displayCollection = noRowsOnScopedPage
    ? ({ ...collection, uiState: "empty" } as typeof collection)
    : collection;
  const posture = collection.uiState === "loading" || collection.state.status === "idle"
    ? "loading"
    : collection.uiState === "error"
      ? postureForCoachingError(collection.state.error)
      : collection.total === 0 && !hasScopedView
        ? "empty"
        : null;
  const gated = posture !== null;
  const showToolbar = posture === null || posture === "empty";

  return (
    <PageFrame title="Sessions" subtitle="Session schedule across cohorts in your scope." className="col-span-12">
      <section aria-label="Sessions" className="flex flex-col gap-4" data-testid="sessions-page">
        {showToolbar && gated ? (
          <CollectionToolbar
            collection={collection}
            searchPlaceholder="Search by title or cohort…"
            primaryAction={
              <Link to="/calendar" className="no-underline">
                <Button variant="secondary">Calendar view</Button>
              </Link>
            }
            className="rounded-xl border border-line"
            stableChipNames
          />
        ) : null}

        <div className="rounded-xl border border-line bg-white">
          {gated ? (
            <HonestState
              posture={posture ?? "loading"}
              gate={honestGateFromError(collection.state.error)}
              reference={referenceFromError(collection.state.error)}
              onRetry={collection.refresh}
              icon={<IconCalendar size={22} />}
              title={posture === "empty" ? "No sessions scheduled" : undefined}
              message={
                posture === "empty"
                  ? "Sessions scheduled in your scope will appear here. Create a session series from a cohort to get started."
                  : undefined
              }
            />
          ) : (
            /* F3: real @vjal/ui ProTable-1 recipe export under Page-1 shell. */
            <ProTable
              collection={displayCollection}
              primaryAction={
                <Link to="/calendar" className="no-underline">
                  <Button variant="secondary">Calendar view</Button>
                </Link>
              }
              className="p-0"
              tableProps={{
                caption: "Sessions in the current view",
                emptyTitle: hasScopedView ? "No sessions match your search" : "No sessions scheduled",
                emptyMessage: hasScopedView
                  ? `No matching sessions on this page. ${collection.total} total session${collection.total === 1 ? "" : "s"} match the current filters.`
                  : "Sessions scheduled in your scope will appear here."
              }}
              stableChipNames
            />
          )}
        </div>
      </section>
    </PageFrame>
  );
}
